/**
 * Safe date formatting utilities.
 *
 * MySQL (and other engines) can return invalid Date objects for values like
 * '0000-00-00' or '0000-00-00 00:00:00'. Calling .toISOString() or
 * .toLocaleString() on those throws RangeError: Invalid time value.
 */

/** Check whether a Date object holds a valid timestamp. */
export function isValidDate(d: Date): boolean {
  return !isNaN(d.getTime());
}

/** Fallback string shown when a Date is invalid. */
const INVALID_DATE_DISPLAY = 'Invalid Date';

/** Safe .toISOString() — returns fallback for invalid dates. */
export function safeToISOString(d: Date): string {
  return isValidDate(d) ? d.toISOString() : INVALID_DATE_DISPLAY;
}

/** Safe .toLocaleString() — returns fallback for invalid dates. */
export function safeToLocaleString(d: Date): string {
  return isValidDate(d) ? d.toLocaleString() : INVALID_DATE_DISPLAY;
}

/** Safe ISO string sliced to datetime-local format (YYYY-MM-DDThh:mm). */
export function safeToDatetimeLocal(d: Date): string {
  return isValidDate(d) ? d.toISOString().slice(0, 16) : '';
}

/** Safe ISO string sliced to seconds precision (YYYY-MM-DDThh:mm:ss). */
export function safeToISOSeconds(d: Date): string {
  return isValidDate(d) ? d.toISOString().slice(0, 19).replace('T', ' ') : INVALID_DATE_DISPLAY;
}
